import type { ApplicationField, SectionLibraryDocument } from '@/types'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  serverTimestamp,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import type { SectionLibraryFormValues } from '../types'

const col = () => typedCollection<SectionLibraryDocument>('sectionLibrary')
const doc = (id: string) => typedDoc<SectionLibraryDocument>('sectionLibrary', id)

interface CreateInput extends SectionLibraryFormValues {
  fields: ApplicationField[]
}

export const adminSectionLibraryService = {
  async getAll(): Promise<Array<SectionLibraryDocument & { id: string }>> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },

  async getByCategory(category: string): Promise<Array<SectionLibraryDocument & { id: string }>> {
    return getCollection(col(), where('category', '==', category), orderBy('createdAt', 'desc'))
  },

  async getById(id: string): Promise<SectionLibraryDocument | null> {
    return getDocument(doc(id))
  },

  async create(data: CreateInput): Promise<string> {
    const payload = buildPayload(data)
    const ref = await addDoc(col(), {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as SectionLibraryDocument)
    return ref.id
  },

  async updateMetadata(id: string, data: SectionLibraryFormValues): Promise<void> {
    const payload: Partial<SectionLibraryDocument> = {
      sectionId: data.sectionId,
      title: data.title,
      ...(data.description ? { description: data.description } : {}),
      ...(data.category ? { category: data.category } : {}),
    }
    await updateDocument(doc(id), { ...payload, updatedAt: serverTimestamp() } as unknown as Partial<SectionLibraryDocument>)
  },

  async updateAll(
    id: string,
    data: SectionLibraryFormValues & { fields: ApplicationField[] }
  ): Promise<void> {
    const payload: Partial<SectionLibraryDocument> = {
      sectionId: data.sectionId,
      title: data.title,
      ...(data.description ? { description: data.description } : {}),
      ...(data.category ? { category: data.category } : {}),
      fields: data.fields.map((f, i) => stripUndefined({ ...f, id: '', order: i })),
    }
    await updateDocument(doc(id), { ...payload, updatedAt: serverTimestamp() } as unknown as Partial<SectionLibraryDocument>)
  },

  async delete(id: string): Promise<void> {
    await deleteDocument(doc(id))
  },
}

function buildPayload(data: CreateInput): Omit<SectionLibraryDocument, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    sectionId: data.sectionId,
    title: data.title,
    ...(data.description ? { description: data.description } : {}),
    ...(data.category ? { category: data.category } : {}),
    fields: data.fields.map((f, i) => stripUndefined({ ...f, id: '', order: i })),
  }
}

function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      out[k] = stripUndefined(v)
    }
    return out as T
  }
  return value
}
